/**
 * Rate Limit Middleware for OpenAPI Client
 * 
 * Middleware that handles 429 rate limit errors with user-friendly toast notifications.
 * Extracts retry-after information and shows clear guidance to users.
 */

import type { Middleware, ResponseContext } from '@/api/generated/client/runtime';

// Store reference to toast function (will be set by api-client)
let toastFn: ((props: { title?: string; description?: string; variant?: 'default' | 'destructive' }) => void) | null = null;

/**
 * Set the toast function to use for displaying rate limit notifications
 * Should be called once during app initialization
 */
export function setToastFunction(fn: typeof toastFn) {
  toastFn = fn;
}

/**
 * Create a middleware that handles rate limit errors (429) with user-friendly notifications
 * 
 * @returns Middleware instance
 */
export function createRateLimitMiddleware(): Middleware {
  return {
    post: async (context: ResponseContext): Promise<Response | void> => {
      const { response } = context;
      
      // Handle rate limit errors (429)
      if (response.status === 429) {
        console.log('[Rate Limit Middleware] Rate limit exceeded for:', context.url);
        
        // Extract retry information from standard rate limit headers
        const retryAfter = response.headers.get('Retry-After'); // Seconds to wait
        const rateLimitReset = response.headers.get('RateLimit-Reset'); // Unix timestamp
        
        // Parse response body for detailed error message
        let errorMessage = 'Too many requests. Please try again later.';
        let errorTitle = 'Rate Limit Exceeded';
        
        try {
          const data = await response.clone().json();
          
          // Extract backend error message
          if (data?.message) {
            errorMessage = data.message;
          } else if (data?.error?.message) {
            errorMessage = data.error.message;
          }
          
          // Customize title based on error code
          if (data?.error === 'TOO_MANY_AUTH_ATTEMPTS') {
            errorTitle = 'Too Many Login Attempts';
          } else if (data?.error === 'TOO_MANY_RESET_ATTEMPTS') {
            errorTitle = 'Too Many Password Reset Requests';
          } else if (data?.error === 'TOO_MANY_INVITES') {
            errorTitle = 'Invite Limit Reached';
          } else if (data?.error === 'TOO_MANY_UPLOADS') {
            errorTitle = 'Upload Limit Exceeded';
          } else if (data?.error === 'TOO_MANY_WORKFLOWS') {
            errorTitle = 'Workflow Limit Reached';
          }
        } catch {
          // Use default message if JSON parse fails
          console.warn('[Rate Limit Middleware] Failed to parse error response body');
        }
        
        // Build user-friendly description with wait time
        let description = errorMessage;
        
        if (retryAfter) {
          const seconds = parseInt(retryAfter);
          if (!isNaN(seconds)) {
            if (seconds < 60) {
              description += ` Please wait ${seconds} second${seconds === 1 ? '' : 's'}.`;
            } else if (seconds < 3600) {
              const minutes = Math.ceil(seconds / 60);
              description += ` Please wait ${minutes} minute${minutes === 1 ? '' : 's'}.`;
            } else {
              const hours = Math.ceil(seconds / 3600);
              description += ` Please wait ${hours} hour${hours === 1 ? '' : 's'}.`;
            }
          }
        } else if (rateLimitReset) {
          // Calculate time until reset from Unix timestamp
          const resetTimestamp = parseInt(rateLimitReset);
          if (!isNaN(resetTimestamp)) {
            const now = Math.floor(Date.now() / 1000);
            const secondsUntilReset = resetTimestamp - now;
            
            if (secondsUntilReset > 0) {
              if (secondsUntilReset < 60) {
                description += ` Try again in ${secondsUntilReset} second${secondsUntilReset === 1 ? '' : 's'}.`;
              } else if (secondsUntilReset < 3600) {
                const minutes = Math.ceil(secondsUntilReset / 60);
                description += ` Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
              } else {
                const hours = Math.ceil(secondsUntilReset / 3600);
                description += ` Try again in ${hours} hour${hours === 1 ? '' : 's'}.`;
              }
            }
          }
        }
        
        // Show toast notification if available
        if (toastFn) {
          toastFn({
            title: errorTitle,
            description,
            variant: 'destructive',
          });
        } else {
          console.warn('[Rate Limit Middleware] Toast function not set, cannot display notification');
          console.warn(`Rate limit: ${errorTitle} - ${description}`);
        }
        
        // Return undefined to let the original 429 response propagate
        // This allows calling code to handle it if needed
        return undefined;
      }
      
      // For non-429 responses, return undefined to use original response
      return undefined;
    },
  };
}
